import React from "react";
import {useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {
    RiUploadLine, RiUserAddLine, RiSendPlaneFill, RiDeleteBinLine, RiUser3Fill,
} from "react-icons/ri";
import Snackbar from "@material-ui/core/Snackbar";
import {getGroupMembers, changeMemberRole, removeMember, addMembers} from "../../../Services/campaignFunctions";
import {useGroupManager} from "../../Providers/groupManager";
import {initials, avatarColorFor} from "../../helpers/avatar";
import {memberLimit} from "../../helpers/memberLimit";
import ConfirmDialog from "../../Components/ConfirmDialog";
import AddMemberModal from "../../Components/AddMemberModal";
import InviteModal from "../../Components/InviteModal";
import WS from "../../../ws";
import {
    Container, State, HeadRow, Title, SubTitle, HeadActions, GhostBtn, PrimaryBtn,
    CapacityCard, CapacityIcon, CapacityMain, CapacityTop, Bar, BarFill, Stats, Stat, StatValue, StatLabel,
    Toolbar, SearchInput, Chips, Chip,
    Table, Th, Tr, NameCell, Avatar, NameText, NameMain, NameSub, RoleSelect, TagBadge, RowActions, SendBtn, TrashBtn,
} from "./styles";

const formatJoined = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, {year: "numeric", month: "short", day: "numeric"});
};

// buildAddSummary: compõe o texto do toast de resultado (só categorias > 0).
const buildAddSummary = (d, t) => {
    const parts = [];
    if (d.added) parts.push(t("words.add_result_added", {count: d.added}));
    if (d.invited) parts.push(t("words.add_result_invited", {count: d.invited}));
    if (d.recently_left) parts.push(t("words.add_result_recently_left", {count: d.recently_left}));
    if (d.skipped_capacity) parts.push(t("words.add_result_skipped_capacity", {count: d.skipped_capacity}));
    if (d.failed) parts.push(t("words.add_result_failed", {count: d.failed}));
    if (d.invalid) parts.push(t("words.add_result_invalid", {count: d.invalid}));
    return parts.join(" · ");
};

/**
 * Aba Members — VIEW (sub-peça A). Tabela dos participantes REAIS do grupo selecionado
 * (na GroupSidebar), com Capacity + filtros + busca. Ações de escrita desabilitadas (sub-peça B).
 */
const Members = () => {
    const {t} = useTranslation();
    const {id: campaignId} = useParams();
    const {selectedGroup, number, campaign, can} = useGroupManager();
    const [list, setList] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const [filter, setFilter] = React.useState("all"); // all | operators | leads
    const [pendingRemove, setPendingRemove] = React.useState(null); // membro aguardando confirmação
    const [inviteOpen, setInviteOpen] = React.useState(false);
    const [addOpen, setAddOpen] = React.useState(false);
    const [snack, setSnack] = React.useState("");
    const pollRef = React.useRef(null);
    const pollDeadlineRef = React.useRef(0);
    const membersDebounceRef = React.useRef(null); // refetch debounced no WS group_members
    const timersRef = React.useRef([]);
    const clearTimers = () => {
        timersRef.current.forEach(clearTimeout);
        timersRef.current = [];
    };

    // Busca os participantes reais. silent=true não mexe em loading/erro (usado no re-sync pós-ação).
    const fetchMembers = React.useCallback(async ({silent} = {}) => {
        if (!selectedGroup) return;
        if (!silent) {
            setLoading(true);
            setError(false);
        }
        const res = await getGroupMembers(campaignId, selectedGroup.id);
        if (res?.isAxiosError || res instanceof Error) {
            if (!silent) {
                setError(true);
                setList([]);
            }
        } else {
            setList(Array.isArray(res) ? res : []);
        }
        if (!silent) setLoading(false);
    }, [campaignId, selectedGroup]);

    React.useEffect(() => {
        if (!selectedGroup) return undefined;
        fetchMembers();
        return () => clearTimers();
    }, [fetchMembers, selectedGroup]);

    // Após uma ação (role/remover), sincroniza com o dado REAL do backend. A ação aplica no
    // WhatsApp em ~2-12s (jitter anti-detecção), então re-busca em 2 momentos — a UI otimista
    // cobre o intervalo e o refetch confirma o estado final.
    const scheduleSync = () => {
        clearTimers();
        timersRef.current = [
            setTimeout(() => fetchMembers({silent: true}), 5000),
            setTimeout(() => fetchMembers({silent: true}), 12000),
        ];
    };

    // Pending-poll: o add roda espaçado (minutos). Re-busca a cada 30s pra mostrar quem entra,
    // até chegar o evento WS de resultado (sinal primário) ou o cap (escala com o lote).
    const stopPendingPoll = React.useCallback(() => {
        if (pollRef.current) {
            clearTimeout(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    const startPendingPoll = React.useCallback((queued) => {
        stopPendingPoll();
        pollDeadlineRef.current = Date.now() + Math.min(queued * 60000 + 90000, 2100000);
        const tick = () => {
            fetchMembers({silent: true});
            if (Date.now() >= pollDeadlineRef.current) return;
            pollRef.current = setTimeout(tick, 30000);
        };
        pollRef.current = setTimeout(tick, 30000);
    }, [fetchMembers, stopPendingPoll]);

    // Toast de resultado via WebSocket (só do grupo aberto). Para o poll + refetch final.
    React.useEffect(() => {
        if (!selectedGroup) return undefined;
        const unsub = WS.subscribe((msg) => {
            if (msg?.subject === "group_participants_added" && msg.data?.group_id === selectedGroup.id) {
                stopPendingPoll();
                fetchMembers({silent: true});
                setSnack(buildAddSummary(msg.data, t));
            }
            // Contagem mudou em tempo real (entrada/saída) — refetch debounced pra não virar rajada.
            if (msg?.subject === "group_members" && msg.data?.group_id === selectedGroup.id) {
                if (membersDebounceRef.current) clearTimeout(membersDebounceRef.current);
                membersDebounceRef.current = setTimeout(() => fetchMembers({silent: true}), 4000);
            }
        });
        return () => {
            if (typeof unsub === "function") unsub();
            stopPendingPoll();
            if (membersDebounceRef.current) clearTimeout(membersDebounceRef.current);
        };
    }, [selectedGroup, fetchMembers, stopPendingPoll, t]);

    if (!selectedGroup) return <Container><State>{t("words.select_a_group")}</State></Container>;

    const MAX_MEMBERS = memberLimit(campaign);
    const total = list.length;
    const operators = list.filter((m) => m.is_operator).length;
    const leads = total - operators;
    const available = Math.max(0, MAX_MEMBERS - total);
    const pct = MAX_MEMBERS ? (total / MAX_MEMBERS) * 100 : 0;

    const term = search.trim().toLowerCase();
    const filtered = list.filter((m) => {
        if (filter === "operators" && !m.is_operator) return false;
        if (filter === "leads" && m.is_operator) return false;
        if (!term) return true;
        return (m.name || "").toLowerCase().includes(term) || (m.number || "").includes(term);
    });

    const groupName = selectedGroup.name || `#${selectedGroup.id}`;
    // Número da conta (bot): não pode ser rebaixado nem removido — senão o sistema perde o controle do grupo.
    const accountNumber = number?.number ? number.number.split("@")[0] : null;
    // Link de convite da campanha (roteia pra um grupo disponível). Ex: https://wppg.builderall.com/c/123
    const inviteLink = `${process.env.REACT_APP_LINK_API}/c/${campaignId}`;

    const onRole = async (m, admin) => {
        const prev = list;
        setList((l) => l.map((x) => (x.number === m.number ? {...x, is_admin: admin} : x)));
        const res = await changeMemberRole(campaignId, selectedGroup.id, m.number, admin);
        if (res?.isAxiosError || res instanceof Error) setList(prev); // reverte
        else scheduleSync(); // sincroniza com o real
    };

    const onAddConfirm = async (numbers) => {
        setAddOpen(false);
        const res = await addMembers(campaignId, selectedGroup.id, numbers);
        if (res?.isAxiosError || res instanceof Error) {
            setSnack(t("words.something_wrong"));
            return;
        }
        const queued = res?.queued || 0;
        if (queued === 0) {
            setSnack(t("words.no_new_numbers"));
            return;
        }
        setSnack(t("words.adding_in_background", {count: queued}));
        startPendingPoll(queued);
    };

    const confirmRemove = async () => {
        const m = pendingRemove;
        setPendingRemove(null);
        if (!m) return;
        const prev = list;
        setList((l) => l.filter((x) => x.number !== m.number));
        const res = await removeMember(campaignId, selectedGroup.id, m.number);
        if (res?.isAxiosError || res instanceof Error) setList(prev); // re-insere
        else scheduleSync(); // sincroniza com o real
    };

    return (
        <Container>
            <HeadRow>
                <div>
                    <Title>{t("words.members")}</Title>
                    <SubTitle>{groupName} · {total}/{MAX_MEMBERS}</SubTitle>
                </div>
                <HeadActions>
                    <GhostBtn disabled><RiUploadLine/> {t("words.import_csv")}</GhostBtn>
                    <PrimaryBtn onClick={() => setInviteOpen(true)} disabled={!can("can_add_members")}><RiUserAddLine/> {t("words.add_member")}</PrimaryBtn>
                </HeadActions>
            </HeadRow>

            <CapacityCard>
                <CapacityIcon><RiUser3Fill/></CapacityIcon>
                <CapacityMain>
                    <CapacityTop>
                        <span>{t("words.capacity")}</span>
                        <span>{total} / {MAX_MEMBERS}</span>
                    </CapacityTop>
                    <Bar><BarFill $pct={pct}/></Bar>
                </CapacityMain>
                <Stats>
                    <Stat><StatValue>{operators}</StatValue><StatLabel>{t("words.operators")}</StatLabel></Stat>
                    <Stat><StatValue>{leads}</StatValue><StatLabel>{t("words.leads")}</StatLabel></Stat>
                    <Stat><StatValue $accent="#22A559">{available}</StatValue><StatLabel>{t("words.available_seats")}</StatLabel></Stat>
                </Stats>
            </CapacityCard>

            <Toolbar>
                <SearchInput
                    value={search}
                    placeholder={t("words.search")}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <Chips>
                    <Chip $active={filter === "all"} onClick={() => setFilter("all")}>{t("words.all")}</Chip>
                    <Chip $active={filter === "operators"} onClick={() => setFilter("operators")}>{t("words.operators")}</Chip>
                    <Chip $active={filter === "leads"} onClick={() => setFilter("leads")}>{t("words.leads")}</Chip>
                </Chips>
            </Toolbar>

            <Table>
                <Th>
                    {/* seleção em massa (bulk) — desativado por enquanto, reativar depois */}
                    {/* <span><input type="checkbox" disabled/></span> */}
                    <span>{t("words.name")}</span>
                    <span>{t("words.number")}</span>
                    <span>{t("words.joined")}</span>
                    <span>{t("words.role")}</span>
                    <span>{t("words.tag")}</span>
                    <span/>
                </Th>

                {loading && <State>{t("words.loading")}</State>}
                {!loading && error && <State>{t("words.something_wrong")}</State>}
                {!loading && !error && filtered.length === 0 && <State>{t("words.no_members")}</State>}
                {!loading && !error && filtered.map((m, i) => {
                    const isAccount = accountNumber && m.number === accountNumber;
                    return (
                    <Tr key={(m.number || "") + i}>
                        {/* seleção em massa (bulk) — desativado por enquanto, reativar depois */}
                        {/* <span><input type="checkbox" disabled/></span> */}
                        <NameCell>
                            <Avatar $color={avatarColorFor(m.number || m.name)}>{initials(m.name)}</Avatar>
                            <NameText>
                                <NameMain>{m.name}</NameMain>
                                <NameSub>{m.is_operator ? t("words.operator") : t("words.lead")}</NameSub>
                            </NameText>
                        </NameCell>
                        <span>{m.number}</span>
                        <span>{formatJoined(m.joined)}</span>
                        <span>
                            <RoleSelect
                                $admin={m.is_admin}
                                value={m.is_admin ? "admin" : "member"}
                                disabled={isAccount || !can("can_edit_group")}
                                onChange={(e) => onRole(m, e.target.value === "admin")}
                            >
                                <option value="admin">{t("words.administrator")}</option>
                                <option value="member">{t("words.member")}</option>
                            </RoleSelect>
                        </span>
                        <span><TagBadge $operator={m.is_operator}>{m.is_operator ? t("words.operator") : t("words.lead")}</TagBadge></span>
                        <RowActions>
                            <SendBtn
                                as="a"
                                href={`https://wa.me/${m.number}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={t("words.message")}
                            ><RiSendPlaneFill/></SendBtn>
                            {!isAccount && can("can_remove_members") && (
                                <TrashBtn onClick={() => setPendingRemove(m)} aria-label={t("words.remove_from_group")}><RiDeleteBinLine/></TrashBtn>
                            )}
                        </RowActions>
                    </Tr>
                    );
                })}
            </Table>

            <ConfirmDialog
                open={!!pendingRemove}
                title={t("words.remove_from_group")}
                message={t("words.remove_member_confirm")}
                confirmLabel={t("words.remove_from_group")}
                danger
                onConfirm={confirmRemove}
                onCancel={() => setPendingRemove(null)}
            />

            <InviteModal
                open={inviteOpen}
                link={inviteLink}
                onClose={() => setInviteOpen(false)}
                onAdvanced={() => { setInviteOpen(false); setAddOpen(true); }}
            />
            <AddMemberModal
                open={addOpen}
                onCancel={() => setAddOpen(false)}
                onConfirm={onAddConfirm}
                onBack={() => { setAddOpen(false); setInviteOpen(true); }}
                available={available}
            />
            <Snackbar
                open={!!snack}
                autoHideDuration={6000}
                onClose={() => setSnack("")}
                message={snack}
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
            />
        </Container>
    );
};

export default Members;
